/**
 * API クライアント。外部のプログラムがこの基盤の API を叩くための資格情報。
 *
 * 実体は **Keycloak のサービスアカウント付きクライアント**(`client_credentials`)。
 * 権限はスイートのロールをそのまま使う(docs/roles.md)。
 * **secret は発行のときだけ**表示する(あとからは見られない。無くしたら再発行)。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { ApiClientRow } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  errorText,
  useIdpApiClient,
  useIdpApiClients,
  useIdpPerms,
  useInvalidateIdp,
  useScopeOptions,
} from "./hooks";

const { Text, Paragraph } = Typography;

/** secret はこの1回だけ。閉じたら二度と出ない。 */
function showSecret(clientId: string, secret: string) {
  Modal.info({
    title: `${clientId} の client secret(この1回だけ)`,
    width: 560,
    content: (
      <>
        <Paragraph copyable={{ text: secret }} style={{ wordBreak: "break-all" }}>
          <Text code>{secret}</Text>
        </Paragraph>
        <Text type="secondary">
          この画面を閉じると二度と表示できない。無くしたら再発行する(前の secret は使えなくなる)。
        </Text>
      </>
    ),
    okText: "控えた",
  });
}

function AddModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const scopeOpts = useScopeOptions();
  const invalidate = useInvalidateIdp();
  const [form] = Form.useForm<{ client_id: string; note: string; scopes: string[] }>();

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const create = useMutation({
    mutationFn: (v: { client_id: string; note: string; scopes: string[] }) =>
      api.createIdpApiClient({ ...v, note: v.note ?? "" }),
    onSuccess: (res) => {
      invalidate();
      showSecret(res.clientId, res.secret);
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal
      open={open}
      title="API クライアントの追加"
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText="作る"
      cancelText="やめる"
      confirmLoading={create.isPending}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
        <Form.Item
          name="client_id"
          label="クライアント ID(あとから変えられない)"
          extra="英小文字・数字・`-` `_` の 3〜60 文字"
          rules={[{ required: true, message: "クライアント ID は必須" }]}
        >
          <Input maxLength={60} placeholder="batch-tool" />
        </Form.Item>
        <Form.Item name="note" label="用途のメモ">
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
        <Form.Item
          name="scopes"
          label="権限(トークンに載るロール)"
          rules={[{ required: true, message: "権限を1つ以上選ぶ" }]}
        >
          <Select mode="multiple" allowClear options={scopeOpts} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ClientDrawer({ id, onClose }: { id: number | null; onClose: () => void }) {
  const api = useApi();
  const scopeOpts = useScopeOptions();
  const invalidate = useInvalidateIdp();
  const detail = useIdpApiClient(id);
  const [form] = Form.useForm<{ note: string; scopes: string[] }>();
  const d = detail.data;

  useEffect(() => {
    if (d) form.setFieldsValue({ note: d.note, scopes: d.scopes });
  }, [d, form]);

  const save = useMutation({
    mutationFn: (v: { note: string; scopes: string[] }) => api.updateIdpApiClient(id!, v),
    onSuccess: () => {
      invalidate();
      void message.success("保存した");
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const rotate = useMutation({
    mutationFn: () => api.rotateIdpApiClientSecret(id!),
    onSuccess: (res) => {
      invalidate();
      showSecret(res.clientId, res.secret);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteIdpApiClient(id!),
    onSuccess: (res) => {
      invalidate();
      if (res.warning) void message.warning(res.warning);
      else void message.success(`${res.client_id} を削除した`);
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Drawer
      open={!!id}
      width={720}
      onClose={onClose}
      destroyOnClose
      title={d ? d.clientId : "API クライアント"}
      extra={
        <Space>
          <Popconfirm
            title="この API クライアントを削除する"
            description="Keycloak のクライアントも消える(呼び出しはすぐ通らなくなる)"
            okText="削除する"
            cancelText="やめる"
            onConfirm={() => remove.mutate()}
          >
            <Button danger loading={remove.isPending}>
              削除
            </Button>
          </Popconfirm>
          <Button type="primary" loading={save.isPending} onClick={() => void form.submit()}>
            保存
          </Button>
        </Space>
      }
    >
      {detail.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="API クライアントを取得できなかった"
          description={errorText(detail.error)}
        />
      )}

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="クライアント ID">
            <Text code>{d?.clientId ?? ""}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="client secret">
            <Flex gap={8} align="center">
              <Text code>{d?.secret ?? ""}</Text>
              <Popconfirm
                title="secret を再発行する"
                description="いまの secret はすぐ使えなくなる。呼び出し側の差し替えが要る"
                okText="再発行する"
                cancelText="やめる"
                onConfirm={() => rotate.mutate()}
              >
                <Button size="small" loading={rotate.isPending}>
                  再発行
                </Button>
              </Popconfirm>
            </Flex>
          </Descriptions.Item>
          <Descriptions.Item label="状態">{d?.status ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="作成">
            {d?.createdAt ?? "—"}({d?.createdBy ?? ""})
          </Descriptions.Item>
          <Descriptions.Item label="最終利用">{d?.lastUsedAt ?? "まだ無い"}</Descriptions.Item>
        </Descriptions>

        <Form form={form} layout="vertical">
          <Form.Item name="note" label="用途のメモ">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="scopes" label="権限(トークンに載るロール)">
            <Select mode="multiple" allowClear options={scopeOpts} />
          </Form.Item>
        </Form>

        <Card size="small" title="使い方(client_credentials)">
          <Paragraph type="secondary">
            トークンを取って `Authorization: Bearer` で呼ぶ。secret は発行のときだけ表示する。
          </Paragraph>
          <Input.TextArea
            readOnly
            autoSize
            value={d?.usage.curl ?? ""}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
        </Card>
      </Space>
    </Drawer>
  );
}

export function ApiClientsPage() {
  const { canAdmin } = useIdpPerms();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const query = useIdpApiClients({ limit: pageSize, offset: (page - 1) * pageSize });

  const columns: ColumnsType<ApiClientRow> = [
    {
      title: "クライアント ID",
      width: 240,
      dataIndex: "clientId",
      render: (v: string, row) => <a onClick={() => setOpenId(row.id)}>{v}</a>,
    },
    { title: "用途", width: 320, dataIndex: "note", ellipsis: true },
    {
      title: "権限",
      dataIndex: "scopes",
      width: 320,
      render: (v: string[]) => v.map((s) => <Tag key={s}>{s}</Tag>),
    },
    { title: "作成者", dataIndex: "createdBy", width: 160 },
    {
      title: "最終利用",
      dataIndex: "lastUsedAt",
      width: 240,
      render: (v: string | null) => v ?? "まだ無い",
    },
    { title: "状態", dataIndex: "status", width: 110, render: (v: string) => <Tag>{v}</Tag> },
  ];

  if (!canAdmin)
    return (
      <Alert
        type="warning"
        showIcon
        message="API クライアントは idp:admin だけが見られる"
        description="資格情報の管理なので、閲覧も管理者に限っている。"
      />
    );

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          API クライアント
        </Typography.Title>
        <Button type="primary" onClick={() => setAdding(true)}>
          API クライアントの追加
        </Button>
      </Flex>

      <Paragraph type="secondary">
        外部のプログラム用の資格情報。人のアカウントは使わない。
        権限はスイートのロールをそのまま付ける。
      </Paragraph>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="API クライアントを取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<ApiClientRow>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          loading={query.isFetching}
          columns={columns}
          dataSource={query.data?.items ?? []}
          locale={{ emptyText: "API クライアントはまだ無い" }}
          pagination={{
            current: page,
            pageSize,
            total: query.data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `全 ${t} 件`,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>

      <AddModal open={adding} onClose={() => setAdding(false)} />
      <ClientDrawer id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
